import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '../../features/authentication/hooks/useAuth';
import { useNavigationConfig } from '../../router/hooks/useRouteConfig';
import { RoleContextSelect } from '../../features/role-context';

interface NavigationItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  isBottomItem?: boolean;
  isLogout?: boolean;
  disabled?: boolean;
  isExternal?: boolean;
}

interface NavigationItemsProps {
  onItemClick?: () => void; // Callback for when item is clicked (for mobile drawer close)
  variant?: 'desktop' | 'mobile'; // Visual variant
}

export const NavigationItems: React.FC<NavigationItemsProps> = ({
  onItemClick,
  variant = 'mobile',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: logout } = useLogout();
  const { topItems, bottomItems } = useNavigationConfig();

  const handleItemClick = (item: NavigationItem) => {
    if (item.disabled) {
      return;
    }
    if (item.isLogout) {
      logout();
    } else if (item.isExternal) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.href);
    }
    onItemClick?.();
  };

  const isItemActive = (href: string) => {
    return location.pathname === href || (href !== '/' && location.pathname.startsWith(href));
  };

  const getItemStyles = (item: NavigationItem, isActive: boolean) => {
    const baseStyles = {
      borderRadius: variant === 'desktop' ? 3 : 2,
      mx: variant === 'desktop' ? 0.5 : 1,
      mb: 0.5,
      height: variant === 'desktop' ? 40 : 44,
      px: variant === 'desktop' ? 1.25 : 1,
      opacity: item.disabled ? 0.5 : 1,
      cursor: item.disabled ? 'default' : 'pointer',
    };

    // Use theme-aware colors for both desktop and mobile
    const activeBackground = theme.palette.secondary.light;
    const hoverBackground = item.isLogout
      ? theme.palette.error.light + '20'
      : theme.palette.secondary.light + (theme.palette.mode === 'dark' ? 'CC' : 'B9');

    if (variant === 'desktop') {
      return {
        ...baseStyles,
        backgroundColor: isActive ? activeBackground : 'transparent',
        '&:hover': {
          backgroundColor: item.disabled ? 'transparent' : hoverBackground,
        },
      };
    } else {
      // Mobile uses the same colors but with MUI's selected state
      return {
        ...baseStyles,
        '&.Mui-selected': {
          backgroundColor: activeBackground,
          '&:hover': {
            backgroundColor: item.disabled ? activeBackground : hoverBackground,
          },
        },
        '&:hover': {
          backgroundColor: item.disabled ? 'transparent' : hoverBackground,
        },
      };
    }
  };

  const renderNavigationItem = (item: NavigationItem) => {
    const isActive = !item.isLogout && isItemActive(item.href);

    return (
      <ListItem key={item.label} disablePadding>
        <ListItemButton
          onClick={() => handleItemClick(item)}
          selected={variant === 'mobile' ? isActive : false}
          sx={getItemStyles(item, isActive)}
          disabled={item.disabled}
        >
          <ListItemIcon
            sx={{
              color: item.isLogout ? theme.palette.error.main : 'inherit', // Keep natural color, let background indicate active state
              minWidth: variant === 'desktop' ? 36 : 40,
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.label}
            sx={{
              '& .MuiListItemText-primary': {
                fontWeight: isActive ? 600 : 400,
                color: item.isLogout ? theme.palette.error.main : 'inherit', // Keep natural color, let background indicate active state
                fontSize: variant === 'desktop' ? '0.875rem' : '0.9rem',
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <>
      {/* Main Navigation */}
      <List disablePadding>{topItems.map(renderNavigationItem)}</List>

      {/* Bottom Navigation */}
      <List disablePadding sx={{ mt: 'auto' }}>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <RoleContextSelect variant="sidebar" size="small" sx={{ mx: 0 }} />
        </Box>
        {bottomItems.map(renderNavigationItem)}
      </List>
    </>
  );
};
